import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Esteiras abertas em PARALELO num veículo (at-99, ADR 0050).
 *
 * O veículo tem uma esteira **principal** (a etapa: onde ele está e quando sai daí) e zero ou
 * mais **paralelas**: serviços em curso em outra esteira, abertos pelo Chefe, enquanto o veículo
 * permanece na etapa em que está. O caso que criou a feature: veículo em Desmontagem com um
 * montador, e um mecânico precisa trabalhar nele — antes disso a oficina movia o veículo para
 * Mecânica e de volta, o que corrompia a permanência por esteira e reiniciava o prazo da etapa.
 *
 * 🔴 **Nenhuma função deste arquivo tem catch.** "Este veículo não tem esteira em paralelo" e
 * "não consegui perguntar" levam a conclusões opostas: com o erro engolido, o Chefe abre a mesma
 * esteira de novo (409) ou conclui que o mecânico não está trabalhando. Quem trata o erro é a
 * tela, que precisa distinguir os dois estados (regra da at-60).
 */

/** Quem está com a mão no carro naquela esteira, agora. */
export interface TrabalhandoNaParalela {
  sessionId: string;
  collaboratorId: string | null;
  collaboratorName: string | null;
  status: string;
}

export interface EsteiraParalela {
  id: string;
  esteiraId: string;
  nome: string;
  cor: string | null;
  motivo: string | null;
  abertaEm: string;
  abertaPorId: string | null;
  abertaPorNome: string | null;
  /**
   * `true` = vigente. Campo explícito e não derivado de `encerradaEm` porque a lista mistura
   * vigentes e encerradas recentes, e é ele que decide a renderização.
   */
  vigente: boolean;
  /** Só nas encerradas. */
  encerradaEm?: string | null;
  encerradaMotivo?: string | null;
  encerradaPorNome?: string | null;
  trabalhando?: TrabalhandoNaParalela[];
}

export interface EsteirasParalelasDoVeiculo {
  principal: { esteiraId: string; nome: string; sistema: boolean };
  vigentes: EsteiraParalela[];
  /**
   * Encerradas nos últimos 30 dias. ⚠️ Aparecem de propósito: é o que explica hora já apontada
   * numa esteira pela qual o veículo nunca passou.
   */
  encerradas: EsteiraParalela[];
}

export const listarEsteirasParalelas = async (
  carId: string,
): Promise<EsteirasParalelasDoVeiculo> => {
  const { data } = await axios.get(`${BACKEND_URL}/cars/${carId}/esteiras-paralelas`, {
    headers: authHeader(),
  });
  return data;
};

/**
 * Abre a esteira em paralelo. Só o Chefe de Oficina (ADMIN por bypass).
 *
 * O veículo **não se move**: a principal não muda, o prazo da etapa não reinicia e o card não
 * troca de coluna no kanban. `motivo` é opcional.
 *
 * Erros que a tela precisa distinguir, porque cada um pede uma ação diferente:
 * - `CAR_NOT_RELEASED` — a Administração não liberou aquela esteira para este veículo;
 * - `PRINCIPAL_NAO_ACEITA_PARALELA` — o veículo está numa etapa de fluxo livre (Aguardando);
 * - `ESTEIRA_NAO_ACEITA_PARALELA` — a esteira escolhida é de sistema ou não recebe colaborador;
 * - `PARALELA_JA_ABERTA` / `ESTEIRA_JA_E_A_PRINCIPAL` — não há o que fazer.
 */
export const abrirEsteiraParalela = async (
  carId: string,
  payload: { esteiraId: string; motivo?: string },
): Promise<EsteiraParalela> => {
  const { data } = await axios.post(
    `${BACKEND_URL}/cars/${carId}/esteiras-paralelas`,
    payload,
    { headers: authHeader() },
  );
  return data;
};

/**
 * Encerra a esteira aberta em paralelo. Só o Chefe de Oficina.
 *
 * ⚠️ Com atividade em curso, o backend responde `409 PARALELA_COM_TRABALHO` **sem gravar nada**,
 * nomeando quem seria interrompido — a tela precisa mostrar isso e reenviar com
 * `confirmar: true`. Aqui, diferente da revogação de apoio da at-97, encerrar significa que o
 * serviço PARA: as horas de quem está trabalhando são fechadas e vão para aprovação.
 */
export const encerrarEsteiraParalela = async (
  carId: string,
  paralelaId: string,
  confirmar = false,
): Promise<{ encerrada: boolean; esteiraNome: string | null; sessoesEncerradas: number }> => {
  const { data } = await axios.delete(
    `${BACKEND_URL}/cars/${carId}/esteiras-paralelas/${paralelaId}`,
    { headers: authHeader(), data: { confirmar } },
  );
  return data;
};
